// Login.jsx
import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./authLayout";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import apiClient, { API_BASE_URL } from "../api/config";
import { useAuth } from "../contexts/AuthContext";
import { ROUTES } from "../routes";
import { getDefaultHomeRoute } from "../utils/clientMsp";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Login using API endpoint (CSRF exempt)
  const loginUser = async ({ email, password, rememberMe }) => {
    const response = await apiClient.post("/api/login/",
      { email, password, remember_me: rememberMe },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter valid credentials.");
      return;
    }

    setLoading(true);

    try {
      const res = await loginUser({ email, password, rememberMe });

      if (res.success) {
        console.log("LOGIN SUCCESS →", res);

        // Update auth context
        login(res.user, rememberMe);
        
        navigate(getDefaultHomeRoute(res.user));
      } else {
        setError(res.error || "Login failed.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.error || err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Welcome to OneView" subtitle="Sign in to access your dashboard">
      <Form onSubmit={handleSubmit}>
        {/* ERROR */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3"
            style={{
              background: "rgba(239,68,68,0.12)",
              color: "#ef4444",
              padding: "10px 14px",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {error}
          </motion.div>
        )}

        <Form.Group className="mb-3">
          <Form.Label style={{ color: "var(--color-text-light)" }}>Email</Form.Label>
          <Form.Control
            type="email"
            value={email}
            placeholder="Enter your email"
            onChange={(e) => setEmail(e.target.value)}
            className="auth-input"
            style={{
              background: "var(--color-bg)",
              color: "var(--color-text)",
            }}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Form.Label style={{ color: "var(--color-text-light)", marginBottom: 0 }}>Password</Form.Label>
            <button
              type="button"
              onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#6f42c1",
                background: "none",
                border: "none",
                padding: 0,
              }}
            >
              Forgot password?
            </button>
          </div>
          <div style={{ position: "relative" }}>
            <Form.Control
              type={showPassword ? "text" : "password"}
              value={password}
              placeholder="Enter your password"
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              style={{
                background: "var(--color-bg)",
                color: "var(--color-text)",
                paddingRight: 40,
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                padding: 0,
                display: "flex",
                alignItems: "center",
                color: "var(--color-text-light)",
                cursor: "pointer",
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </Form.Group>

        <Form.Group className="mb-5">
          <Form.Check
            type="checkbox"
            id="remember-me"
            label="Remember me"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{ color: "var(--color-text-light)", fontSize: 14 }}
          />
        </Form.Group>

        <motion.button
          whileHover={{ scale: !loading ? 1.02 : 1 }}
          whileTap={{ scale: !loading ? 0.98 : 1 }}
          type="submit"
          className="w-100"
          disabled={loading}
          style={{
            background: "#6f42c1",
            border: "none",
            borderRadius: 12,
            padding: "10px 0",
            color: "#fff",
            fontWeight: 600,
            fontSize: 15,
            marginBottom: "1rem",
            opacity: loading ? 0.8 : 1,
            transition: "0.2s",
          }}
        >
          {loading ? "Logging in..." : "Login"}
        </motion.button>

        {/* Azure SSO Login for Superadmin */}
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            marginBottom: "0.5rem",
            color: "var(--color-text-light)",
            fontSize: 14
          }}>
            <div style={{ flex: 1, height: 1, background: "var(--color-border)" }}></div>
            <span style={{ margin: "0 12px" }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "var(--color-border)" }}></div>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => {
              const frontendUrl = window.location.origin;
              const redirectPath = ROUTES.SUPERADMIN_OVERVIEW;
              const nextUrl = `${frontendUrl}${ROUTES.AUTH_CALLBACK}?redirect=${encodeURIComponent(redirectPath)}`;
              window.location.href = `${API_BASE_URL}/azure_auth/login?next=${encodeURIComponent(nextUrl)}`;
            }}
            className="w-100"
            style={{
              background: "#fff",
              border: "1px solid #8c8c8c",
              borderRadius: 4,
              padding: "10px 0",
              color: "#5e5e5e",
              fontWeight: 600,
              fontSize: 15,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              transition: "0.2s",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Sign in with Microsoft
          </motion.button>
        </div>

        {/* Footer disclaimer */}
        <p
          className="text-center mb-0"
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--color-text-light)",
            borderTop: "1px solid var(--color-border)",
            paddingTop: 16,
            marginTop: 8,
          }}
        >
          Access managed by Versetal. Need help?{" "}
          <a href="mailto:95614436.versetalinfo.com@amer.teams.ms" className="auth-link">
            Contact Support
          </a>
        </p>
      </Form>
    </AuthLayout>
  );
}
